import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { APP_CONFIG } from '../../app/config';
import { extensionApi } from '../../api/client';
import { useSessionStore } from '../../session/store';
import { ROUTES } from '../../routes/constants';
import { parseConnectParams, validateConnectRequest, buildApproveRedirectUrl, buildCancelRedirectUrl } from './extensionConnectHelpers';

// Phase 2F: secure Chrome extension connect flow. This page is opened by
// chrome.identity.launchWebAuthFlow with client_id/redirect_uri/
// code_challenge/code_challenge_method/state as query params. The pure
// parsing/validation/redirect-URL helpers live in ./extensionConnectHelpers
// (side-effect-free, unit tested there).

const NOT_CONFIGURED_MESSAGE = 'Extension connection is not available right now. Please try again later or contact support.';
const MISMATCH_MESSAGE = 'This connection request could not be verified. Close this tab and try again from the extension.';
const INVALID_PKCE_MESSAGE = 'This connection request is invalid. Close this tab and try again from the extension.';
const GENERIC_FAILURE_MESSAGE = 'This connection could not be completed. Please try again from the extension.';

function ConnectCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="relative bg-bg-panel border border-bg-border/60 rounded-xl p-6 space-y-3 shadow-modal overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />
          {children}
        </div>
      </div>
    </div>
  );
}

export default function ExtensionConnectPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = useSessionStore((s) => s.mode);
  const token = useSessionStore((s) => s.token);
  const ready = useSessionStore((s) => s.ready);

  const params = useMemo(() => parseConnectParams(searchParams.toString()), [searchParams]);
  const expected = APP_CONFIG.extension;
  const validation = useMemo(() => validateConnectRequest(params, expected), [params, expected]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const previousTitle = document.title;
    document.title = 'Connect Chrome Extension | AlgoSys';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  // Require a real logged-in website session before rendering anything
  // beyond a loading state. Only standalone-token mode has an unambiguous
  // "not logged in" signal; dev-open/algosys-embedded are not primary
  // targets for this top-level-browser-tab flow (see deployment docs).
  useEffect(() => {
    if (mode === 'standalone-token' && ready && !token) {
      const next = `${window.location.pathname}${window.location.search}`;
      navigate(`${ROUTES.LOGIN}?next=${encodeURIComponent(next)}`, { replace: true });
    }
  }, [mode, ready, token, navigate]);

  if (mode === 'standalone-token' && (!ready || !token)) {
    return (
      <ConnectCard>
        <p className="text-xs font-mono text-txt-muted">Loading…</p>
      </ConnectCard>
    );
  }

  if (!validation.ok) {
    const message =
      validation.reason === 'not_configured'
        ? NOT_CONFIGURED_MESSAGE
        : validation.reason === 'mismatch'
        ? MISMATCH_MESSAGE
        : INVALID_PKCE_MESSAGE;
    return (
      <ConnectCard>
        <h1 className="font-bold text-base text-txt-primary">Connection request invalid</h1>
        <p className="text-xs text-bear bg-bear/10 border border-bear/30 rounded-lg px-3 py-2">{message}</p>
      </ConnectCard>
    );
  }

  const handleApprove = async () => {
    setSubmitting(true);
    setError('');
    try {
      const response = await extensionApi.authorize({
        client_id: params.clientId,
        redirect_uri: params.redirectUri,
        code_challenge: params.codeChallenge,
        code_challenge_method: params.codeChallengeMethod,
        state: params.state || undefined,
      });
      // Belt-and-suspenders: only ever navigate using the operator-configured
      // expected redirect_uri, never a value taken from the network response
      // or the raw query string, even though they must already be equal by
      // the time the backend accepts the request.
      if (!response || response.redirect_uri !== expected.redirectUri) {
        setError(GENERIC_FAILURE_MESSAGE);
        setSubmitting(false);
        return;
      }
      window.location.href = buildApproveRedirectUrl(expected.redirectUri, response.code, response.state ?? params.state);
    } catch (_err) {
      setError(GENERIC_FAILURE_MESSAGE);
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    window.location.href = buildCancelRedirectUrl(expected.redirectUri, params.state);
  };

  return (
    <ConnectCard>
      <h1 className="font-bold text-base text-txt-primary">Connect the AlgoSys Chrome Extension</h1>
      <p className="text-xs font-mono text-txt-muted">
        This lets the AlgoSys Chrome extension on this browser act on strategies you choose, on your behalf.
      </p>
      {error && <p className="text-xs text-bear bg-bear/10 border border-bear/30 rounded-lg px-3 py-2">{error}</p>}
      <div className="flex gap-3 pt-1">
        <button type="button" className="btn-primary flex-1 justify-center" onClick={handleApprove} disabled={submitting}>
          {submitting ? 'Connecting…' : 'Approve'}
        </button>
        <button
          type="button"
          className="flex-1 justify-center border border-bg-border/60 rounded-lg text-xs font-mono py-2 text-txt-muted hover:text-txt-primary"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </ConnectCard>
  );
}
